import type { ReactNode } from 'react';

/**
 * CROSS-ROLE-HOME-GRAMMAR-V1 (founder ruling D4, 2026-08-31) -- THE ONE
 * COMPACT DAY SUMMARY.
 *
 * The role home's day facts as a QUIET TEXT RIBBON: value first, then the
 * word, cells divided by internal hairlines, sitting in the header rhythm --
 * never a strip of large tiles. Founder D4: "The doctor does NOT move to the
 * large tiles ... that would make the technically reusable primitive dictate
 * the product." So the DOCTOR'S ribbon grammar is promoted HERE and the front
 * desk's counts move ONTO it: SAME grammar, ROLE-SPECIFIC metrics.
 *
 * REUSE GATE (C2, run BEFORE this file was written -- the DateCommandBar
 * idiom: name why each registered candidate could not carry the job).
 *
 *   - KpiBand (ui/KpiBand.tsx, .ac-kband*) -- the closest candidate, and it
 *     really is a hairline-divided flat-cell band whose own header forbids
 *     floating boxes. It still cannot carry this job on four counts, each
 *     structural rather than cosmetic: it IS a raised surface (border +
 *     card shadow + large radius) where the ribbon is a line of text inside
 *     an existing header; its `title` is REQUIRED where the ribbon names
 *     nothing (D2/D5 forbid adding narrating copy); its cells stack the
 *     label ABOVE the value in a padded block where the ribbon runs the
 *     value BEFORE the word on one line; and it lays cells on an auto-fit
 *     grid with an 11rem (9.5rem compact) minimum track, so a seven-fact
 *     day ribbon would wrap into a multi-row tile grid -- the exact "large
 *     tiles" outcome D4 refuses. An additive variant would have to override
 *     the chrome, the orientation, the padding, the layout AND the divider
 *     mechanism, i.e. every visual rule the band owns. That is not a
 *     variant of KpiBand; it is a different component wearing its name.
 *
 *   - KpiTile (ui/KpiTile.tsx, .ac-tile*) + a third size grade -- the size
 *     axis ('base' | 'hero') only re-grades the VALUE's type. It does not
 *     reach the tile's identity: a tinted severity surface with a 3px inset
 *     edge, label above value, and NO divider at all (it borrows its layout
 *     from the host's tile grid, which is precisely the five-equal-cards
 *     shape D4 and D7 reject). A grade that deletes the surface, deletes
 *     the inset edge, inverts label and value and adds a hairline is not a
 *     size grade. Its tone axis is also the wrong carrier for D7: tone
 *     means severity, and a zero is not a severity.
 *
 * VERDICT: both genuinely fail, so this is a REGISTERED EXTENSION of the
 * summary vocabulary (registry entry component.day-summary-ribbon), owned in
 * the shared ui/ directory so a THIRD role home inherits the grammar without
 * another conformance package. It composes the EXISTING canon only: the
 * --ac-* token layer and the .ac-dayribbon* block in the ONE stylesheet --
 * zero new tokens, zero new colours, no chip tone spent.
 *
 * D7 -- A ZERO DOES NOT SHOUT LIKE A CRISIS. A cell whose count is zero
 * collapses to a visually recessive state; a cell carrying work stays loud.
 * The primitive decides this itself from the value it is given, so the rule
 * cannot drift per role: a numeric 0 (or the string '0') is quiet unless the
 * caller says otherwise, and a caller whose formatted value is semantically
 * empty (a zero money figure, an honest '-') may say so with `quiet`. Same
 * facts, same numbers, weighted by attention.
 *
 * PRESENTATION ONLY. This component owns no state, issues no read, derives
 * nothing and knows nothing about clinics, roles or PHI: values arrive fully
 * formatted and fully fenced from the caller, exactly as KpiBand's contract
 * requires. DISPLAY-ONLY BY CONSTRUCTION -- a cell takes no onClick, because
 * whether a count filters in place or navigates is undefined behaviour
 * (founder, 2026-08-31: the counts stay display-only in this package).
 *
 * Testability (repo idiom -- react-dom/server, no jsdom): pure props in,
 * markup out.
 */

export interface DaySummaryCellDef {
  /** React list key; never rendered. */
  readonly key: string;
  /** The word beside the number ("Waiting", "Needs payment"). */
  readonly label: ReactNode;
  /** The fact, already formatted and already role-fenced by the caller. */
  readonly value: ReactNode;
  /**
   * Force the D7 recessive state for a value the primitive cannot read as a
   * zero (a formatted money figure, a scope-shaped honest dash). Omit and
   * the primitive decides from the value itself.
   */
  readonly quiet?: boolean;
  readonly testId?: string;
}

export interface DaySummaryRibbonProps {
  /** Accessible name for the group (e.g. the day the facts describe). */
  readonly label: string;
  readonly cells: ReadonlyArray<DaySummaryCellDef>;
  /**
   * ONE honest line under the ribbon, for the case the numbers themselves
   * cannot explain: the day read did not land, so every cell prints a dash.
   * SAY IT ONCE -- the reason belongs to the ribbon, not repeated inside
   * every cell. Omit it and the markup is byte-identical to a ribbon that
   * never had one, so a surface with nothing to explain adds nothing.
   */
  readonly seam?: ReactNode;
  readonly testId?: string;
}

/**
 * THE D7 RULE, in one place. A count of zero is quiet; anything else is loud.
 * Exported so a surface can pin the rule rather than re-deriving it, and so
 * the cross-role guard can prove both role homes obey the SAME rule.
 */
export function isQuietDaySummaryValue(cell: DaySummaryCellDef): boolean {
  if (cell.quiet !== undefined) return cell.quiet;
  return cell.value === 0 || cell.value === '0';
}

export default function DaySummaryRibbon({
  label,
  cells,
  seam,
  testId,
}: DaySummaryRibbonProps): JSX.Element {
  const ribbon = (
    <div
      className="ac-dayribbon"
      role="group"
      aria-label={label}
      data-testid={testId}
    >
      {cells.map((cell) => {
        const quiet = isQuietDaySummaryValue(cell);
        return (
          <span
            key={cell.key}
            className={
              quiet ? 'ac-dayribbon__cell ac-dayribbon__cell--quiet' : 'ac-dayribbon__cell'
            }
            data-day-summary={quiet ? 'quiet' : 'live'}
            {...(cell.testId !== undefined ? { 'data-testid': cell.testId } : {})}
          >
            <b className="ac-dayribbon__value">{cell.value}</b>
            {cell.label}
          </span>
        );
      })}
    </div>
  );
  if (seam === undefined || seam === null) return ribbon;
  return (
    <div className="ac-dayribbon-block">
      {ribbon}
      <p className="ac-dayribbon__seam">{seam}</p>
    </div>
  );
}
